import {
  Box,
  Container,
  CssBaseline,
  Divider,
  Typography,
  createTheme,
} from "@mui/material";
import Seo from "../components/Seo";
import AboutCTA from "./AboutUs/AboutCTA";

// Premium PDF-style theme
const theme = createTheme({
  palette: {
    primary: { main: "#1a237e" }, // Brand blue
    secondary: { main: "#d50000" }, // Accent red
    background: { default: "#ffffff" },
    text: { primary: "#1e1e1e", secondary: "#555" },
  },
  typography: {
    fontFamily: '"Times New Roman", "Georgia", "Serif"',
    h4: { fontWeight: 700, fontSize: "2rem", marginBottom: "0.5em" },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      marginTop: "1.5em",
      marginBottom: "0.5em",
    },
    body1: { lineHeight: 1.8, fontSize: "1rem", color: "#1e1e1e" },
    body2: { lineHeight: 1.6, fontSize: "0.95rem", color: "#555" },
  },
});

// Section component
const Section = ({ title, content }) => (
  <Box component="section" sx={{ mb: 3 }}>
    <Typography variant="h5" component="h2">
      {title}
    </Typography>
    {Array.isArray(content) ? (
      content.map((paragraph, idx) => (
        <Typography variant="body1" paragraph key={idx}>
          {paragraph}
        </Typography>
      ))
    ) : (
      <Typography variant="body1" paragraph sx={{ whiteSpace: "pre-line" }}>
        {content}
      </Typography>
    )}
  </Box>
);

const PrivacyPolicyPage = () => {
  return (
    <Box>
      <Seo
        title="Privacy Policy | Burj Al Yarmouk Industries LLC"
        description="Learn how Burj Al Yarmouk Industries LLC collects, uses, stores, and protects your personal information. View our privacy practices, data usage, cookies policy, and user rights."
        url="https://burjalyarmouk.com/privacy-policy"
        image="https://burjalyarmouk.com/og-banner.jpg"
        keywords="privacy policy, data protection UAE, Burj Al Yarmouk Industries LLC privacy, user data policy, industrial company privacy, fabrication company privacy policy"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Privacy Policy",
          url: "https://burjalyarmouk.com/privacy-policy",
          publisher: {
            "@type": "Organization",
            name: "Burj Al Yarmouk Industries LLC",
            url: "https://burjalyarmouk.com/",
            logo: "https://burjalyarmouk.com/favicon.ico",
            sameAs: [
              "https://www.instagram.com/burjalyarmouk/",
              "https://www.linkedin.com/in/burj-al-yarmouk-industries-llc-417570347/",
            ],
          },
          description:
            "Privacy Policy of Burj Al Yarmouk Industries LLC. This page explains how we collect, store, and use your personal information according to UAE regulations.",
        }}
      />

      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary">
            Burj AI — Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: December 2, 2025
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            color="secondary"
            sx={{ fontStyle: "italic" }}
          >
            Important: This Privacy Policy describes how Burj AI collects, uses,
            and protects your personal data. Please read carefully.
          </Typography>
        </Box>

        {/* Sections */}
        <Box component="main">
          {privacyData.sections.map((section, idx) => (
            <Section key={idx} {...section} />
          ))}
        </Box>

        {/* Footer */}
        <Divider sx={{ mt: 6, mb: 2 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          © 2025 Burj AI. All rights reserved.
        </Typography>
      </Container>
      <AboutCTA />
    </Box>
  );
};

// Privacy Policy Data
const privacyData = {
  sections: [
    {
      title: "1. Introduction",
      content:
        "Burj AI respects your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you use our platform and services.",
    },
    {
      title: "2. Information We Collect",
      content: [
        "2.1 Personal information you provide directly, such as name, email, company, and account credentials.",
        "2.2 Usage data including IP address, browser type, device information, and platform interaction data.",
        "2.3 Cookies and tracking technologies to improve user experience and platform performance.",
      ],
    },
    {
      title: "3. How We Use Your Information",
      content: [
        "3.1 To provide and improve our AI platform and services.",
        "3.2 To communicate with you about updates, offers, and support.",
        "3.3 To analyze usage patterns and enhance platform functionality.",
        "3.4 To comply with legal obligations and protect Burj AI’s rights.",
      ],
    },
    {
      title: "4. Data Sharing & Disclosure",
      content: [
        "We do not sell your personal information. We may share data with:",
        "• Service providers assisting with platform operations.",
        "• Legal authorities when required by law.",
        "• In case of mergers, acquisitions, or business transfers.",
      ],
    },
    {
      title: "5. Data Retention",
      content:
        "Burj AI retains personal data only as long as necessary to provide services, comply with legal obligations, or resolve disputes.",
    },
    {
      title: "6. Your Rights",
      content: [
        "You may access, update, or delete your personal data by contacting Burj AI support.",
        "You can opt out of promotional communications at any time.",
      ],
    },
    {
      title: "7. Security",
      content:
        "We implement reasonable technical and organizational measures to protect your personal data from unauthorized access, use, or disclosure.",
    },
    {
      title: "8. Third-Party Links",
      content:
        "Our platform may contain links to third-party websites. We are not responsible for their privacy practices or content.",
    },
    {
      title: "9. Children's Privacy",
      content:
        "Burj AI does not knowingly collect personal information from children under 13. If we learn that we have, we will delete it promptly.",
    },
    {
      title: "10. Changes to This Policy",
      content:
        "We may update this Privacy Policy at any time. We will post the revised version with a new 'Last updated' date. Continued use of our services constitutes acceptance of the updated policy.",
    },
    {
      title: "11. Contact Us",
      content:
        "For questions about this Privacy Policy, please contact us at support@burjai.in or visit our registered office.",
    },
  ],
};

export default PrivacyPolicyPage;
